import React, { useState, useEffect, useCallback, useRef } from 'react';
import PurchaseChallanPrintedTable from '../components/purchase-challan/PurchaseChallanPrintedTable';
import PurchaseChallanActions from '../components/purchase-challan/PurchaseChallanActions';
import PurchaseChallanViewModal from '../components/purchase-challan/PurchaseChallanViewModal';
import PurchaseChallanEditModal from '../components/purchase-challan/PurchaseChallanEditModal';
import PurchaseChallanPrintModal from '../components/purchase-challan/PurchaseChallanPrintModal';
import usePurchaseOrder from '../hooks/usePurchaseOrder';
import { useRole } from '../context/roleContext';
import { USER_ROLES } from '../utils/constants';
import ExportModal from '../components/common/ExportModal';
import { useMessageAlert } from '../components/common/MessageAlert';
import Pagination from '../components/common/Pagination';
import { useTranslation } from 'react-i18next';

const DEFAULT_PAGE_LIMIT = 20;
const PAGE_SIZE_STORAGE_KEY = 'purchase_challan_printed_page_size';

const PurchaseChallanPrintedPage = () => {
    const { t } = useTranslation();
    const {
        purchaseOrders: challans,
        loading,
        fetchPurchaseChallans,
        getPurchaseChallanById,
        editPurchaseChallan,
        exportPurchaseChallans,
        totalPages,
        totalCount,
    } = usePurchaseOrder();

    const { showSuccess, showError } = useMessageAlert();
    const { userRole } = useRole();

    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [selectedChallan, setSelectedChallan] = useState(null);
    const [viewLoading, setViewLoading] = useState(false);

    const [printModalOpen, setPrintModalOpen] = useState(false);
    const [printChallan, setPrintChallan] = useState(null);
    const [printLoading, setPrintLoading] = useState(false);

    // State for Edit Modal
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editChallan, setEditChallan] = useState(null);
    const [editLoading, setEditLoading] = useState(false);

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(() => {
        const saved = localStorage.getItem(PAGE_SIZE_STORAGE_KEY);
        return saved ? Number(saved) : DEFAULT_PAGE_LIMIT;
    });
    const [searchQuery, setSearchQuery] = useState('');
    const [committedSearch, setCommittedSearch] = useState('');

    const today = new Date().toLocaleDateString('en-CA');
    const [fromDate, setFromDate] = useState(today);
    const [toDate, setToDate] = useState(today);
    const [refreshLoading, setRefreshLoading] = useState(false);
    const [showExportModal, setShowExportModal] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);
    const searchDebounceRef = useRef(null);

    const handleSearchChange = (value) => {
        setSearchQuery(value);
        if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = setTimeout(() => {
            setCommittedSearch(value);
            setCurrentPage(1);
        }, 400);
    };

    const handleFromDateChange = (value) => { setFromDate(value); setCurrentPage(1); };
    const handleToDateChange = (value) => { setToDate(value); setCurrentPage(1); };

    const loadChallans = useCallback(async (isRefresh = false) => {
        if (isRefresh) setRefreshLoading(true);
        try {
            const skip = (currentPage - 1) * pageSize;
            await fetchPurchaseChallans(
                {
                    skip,
                    limit: pageSize,
                    challan_printed: true,
                    gst_included: false,
                    ...(committedSearch ? { search: committedSearch } : {}),
                    ...(fromDate ? { from_date: fromDate } : {}),
                    ...(toDate ? { to_date: toDate } : {}),
                },
                { force: isRefresh }
            );
        } catch (err) {
            console.error('Failed to load challans', err);
        } finally {
            if (isRefresh) setRefreshLoading(false);
        }
    }, [fetchPurchaseChallans, currentPage, committedSearch, fromDate, toDate, pageSize]);

    useEffect(() => {
        loadChallans();
    }, [loadChallans]);

    const handleRefresh = () => loadChallans(true);

    const handlePageSizeChange = (newSize) => {
        setPageSize(newSize);
        setCurrentPage(1);
        localStorage.setItem(PAGE_SIZE_STORAGE_KEY, newSize.toString());
    };

    const handleExport = async ({ from_date, to_date, format }) => {
        setExportLoading(true);
        try {
            const result = await exportPurchaseChallans({ from_date, to_date, format, challan_printed: true, gst_included: false });
            if (result.success) {
                const url = URL.createObjectURL(result.data);
                const a = document.createElement('a');
                a.href = url;
                a.download = `purchase-challans-printed-${from_date}-to-${to_date}.${format === 'excel' ? 'xlsx' : 'csv'}`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
                setShowExportModal(false);
            } else {
                showError(result.error || 'Export failed');
            }
        } finally {
            setExportLoading(false);
        }
    };

    const printedChallans = challans || [];

    const handleViewChallan = async (challanNumber) => {
        setViewLoading(true);
        setViewModalOpen(true);
        const result = await getPurchaseChallanById(challanNumber);
        if (result.success) {
            setSelectedChallan(result.data);
        } else {
            showError(t('purchaseChallan.view.empty.message'));
            setViewModalOpen(false);
        }
        setViewLoading(false);
    };

    const handlePrintChallan = async (challanNumber) => {
        setPrintLoading(true);
        setPrintModalOpen(true);
        const result = await getPurchaseChallanById(challanNumber);
        if (result.success) {
            setPrintChallan(result.data);
        } else {
            showError(t('purchaseChallan.view.empty.message'));
            setPrintModalOpen(false);
        }
        setPrintLoading(false);
    };

    const handleEditChallan = async (challanNumber) => {
        setEditLoading(true);
        setEditModalOpen(true);
        const result = await getPurchaseChallanById(challanNumber);
        if (result.success) {
            setEditChallan(result.data);
        } else {
            showError(t('purchaseChallan.edit.errors.fetchFailed'));
            setEditModalOpen(false);
        }
        setEditLoading(false);
    };

    const handleUpdateChallan = async (payload) => {
        if (!editChallan) return;
        setEditLoading(true);
        const targetId = editChallan.advice_id || editChallan.id;
        const result = await editPurchaseChallan(targetId, payload);
        if (result.success) {
            showSuccess(t('purchaseChallan.edit.notifications.success'));
            setEditModalOpen(false);
            setEditChallan(null);
            loadChallans(true);
        } else {
            showError(result.error || t('purchaseChallan.edit.errors.updateFailed'));
        }
        setEditLoading(false);
    };



    return (
        <div className="min-h-screen bg-gray-50 p-3 sm:p-6 w-full max-w-full">
            <div className="mb-8">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900">{t('navigation.pageTitles.purchaseChallanPrinted')}</h1>
                        <p className="mt-2 text-gray-600">{t('navigation.pageSubtitles.purchaseChallanPrinted')}</p>
                    </div>

                    <div className="flex space-x-4">
                        <div className="bg-white px-4 py-2 rounded-lg border border-gray-200">
                            <div className="flex items-center space-x-3">
                                <div className="text-sm text-gray-500">{t('navigation.pageMetrics.totalPrinted')}</div>
                                <div className="text-2xl font-bold text-gray-900">{totalCount}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <PurchaseChallanActions
                showSearch={true}
                searchValue={searchQuery}
                onSearchChange={handleSearchChange}
                loading={loading}
                refreshLoading={refreshLoading}
                onRefresh={handleRefresh}
                fromDate={fromDate}
                toDate={toDate}
                onFromDateChange={handleFromDateChange}
                onToDateChange={handleToDateChange}
                onExport={() => setShowExportModal(true)}
                exportLoading={exportLoading}
                canExport={[USER_ROLES.ADMIN, USER_ROLES.SUPER_ADMIN, USER_ROLES.ACCOUNT_USER, USER_ROLES.OPERATIONS_MANAGER].includes(userRole)}
            />

            <div className="bg-white shadow-sm rounded-lg border border-gray-200 mt-6 overflow-hidden">
                <PurchaseChallanPrintedTable
                    challans={printedChallans}
                    loading={loading}
                    onViewChallan={handleViewChallan}
                    onEditChallan={handleEditChallan}
                    onPrintChallan={handlePrintChallan}
                    userRole={userRole}
                    viewLoading={viewLoading}
                />
            </div>

            <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalCount={totalCount}
                onPageChange={setCurrentPage}
                loading={loading}
                totalLabel="total printed challans"
                pageSize={pageSize}
                onPageSizeChange={handlePageSizeChange}
            />

            {showExportModal && (
                <ExportModal
                    isOpen={showExportModal}
                    onClose={() => setShowExportModal(false)}
                    onExport={handleExport}
                    loading={exportLoading}
                    title={t('common.actions.exportPrintedPurchaseChallans')}
                />
            )}

            {viewModalOpen && (
                <PurchaseChallanViewModal
                    isOpen={viewModalOpen}
                    onClose={() => setViewModalOpen(false)}
                    challan={selectedChallan}
                    loading={viewLoading}
                />
            )}

            {printModalOpen && (
                <PurchaseChallanPrintModal
                    isOpen={printModalOpen}
                    onClose={() => setPrintModalOpen(false)}
                    data={printChallan}
                    loading={printLoading}
                />
            )}

            {editModalOpen && (
                <PurchaseChallanEditModal
                    isOpen={editModalOpen}
                    onClose={() => setEditModalOpen(false)}
                    onSubmit={handleUpdateChallan}
                    challan={editChallan}
                    loading={editLoading}
                />
            )}
        </div>
    );
};

export default PurchaseChallanPrintedPage;
